import React, { useEffect, useMemo, useState } from "react";
import { FaHistory, FaFileAlt, FaFileExcel, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./style.css";
import axios from "axios";
import * as XLSX from "xlsx";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../js/api.js";
import Loader from "../../components/Loader/index.js";

const formatarDataHora = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const formatarCnpjCpf = (valor) => {
  if (!valor) return "";
  const nums = String(valor).replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

// Opções dos filtros de Autor e Evento são derivadas dinamicamente dos
// próprios dados retornados pela API (ver `opcoesAutor` e `opcoesEvento`
// dentro do componente). O frontend NÃO mantém catálogo prévio de eventos.

// ============ Helpers para o campo "campo_alterado" ============
// O backend pode mandar o campo em formatos diferentes. Esta função entende
// todos eles e devolve um objeto canônico { coluna, posicao, tipo }.
//   - "salario"                                      (simples)
//   - "salario (col. 59)"                            (com posição da coluna)
//   - "59|salario"                                   (pipe)
//   - "salario|float"                                (pipe com tipo)
//   - { coluna: "salario", posicao: 59, tipo: "float" } (objeto)
const parsearCampoAlterado = (campo) => {
  if (campo === null || campo === undefined || campo === "") {
    return { coluna: "", posicao: null, tipo: "" };
  }

  if (typeof campo === "object") {
    return {
      coluna: campo.coluna || campo.nome || campo.campo || "",
      posicao: campo.posicao ?? campo.column_id ?? campo.ordinal ?? null,
      tipo: campo.tipo || campo.data_type || "",
    };
  }

  const texto = String(campo).trim();

  const mColPos = /^(.+?)\s*\(\s*col\.?\s*(\d+)\s*\)\s*$/i.exec(texto);
  if (mColPos) {
    return {
      coluna: mColPos[1].trim(),
      posicao: Number(mColPos[2]),
      tipo: "",
    };
  }

  if (texto.includes("|")) {
    const pedacos = texto.split("|").map((p) => p.trim());
    let posicao = null;
    let inicio = 0;
    if (/^\d+$/.test(pedacos[0])) {
      posicao = Number(pedacos[0]);
      inicio = 1;
    }
    const TIPOS_DADO = new Set([
      "int",
      "integer",
      "float",
      "double",
      "decimal",
      "varchar",
      "char",
      "text",
      "date",
      "datetime",
      "timestamp",
      "boolean",
      "bool",
    ]);
    let tipo = "";
    let fim = pedacos.length;
    if (TIPOS_DADO.has(pedacos[fim - 1].toLowerCase())) {
      tipo = pedacos[fim - 1];
      fim -= 1;
    }
    const coluna = pedacos.slice(inicio, fim).join("|");
    return { coluna, posicao, tipo };
  }

  return { coluna: texto, posicao: null, tipo: "" };
};

// Render amigável: "salario (col. 59)" se vier com posição; senão só o nome.
const formatarCampoAlterado = (campo) => {
  const { coluna, posicao } = parsearCampoAlterado(campo);
  if (!coluna) return "—";
  if (posicao) return `${coluna} (col. ${posicao})`;
  return coluna;
};

// ============ Detecção e formatação de tipos (texto/número/data/moeda) ============

const PALAVRAS_CHAVE_MOEDA = [
  "salario",
  "salário",
  "bolsa",
  "auxilio",
  "auxílio",
  "remuneracao",
  "remuneração",
  "valor_bolsa",
  "renda",
  "preco",
  "preço",
  "mensalidade",
  "custo",
];

const ehChaveMoeda = (campo) => {
  if (!campo) return false;
  const { coluna } = parsearCampoAlterado(campo);
  const normalizado = String(coluna || campo)
    .toLowerCase()
    .trim();
  return PALAVRAS_CHAVE_MOEDA.some((p) => normalizado.includes(p));
};

const parseDataSegura = (valor) => {
  if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
  if (typeof valor !== "string") return null;
  const s = valor.trim();
  if (!s) return null;
  const iso =
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (iso) {
    const [, y, m, d, hh = "00", mm = "00", ss = "00"] = iso;
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return isNaN(dt.getTime()) ? null : dt;
  }
  const br =
    /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (br) {
    const [, d, m, y, hh = "00", mm = "00", ss = "00"] = br;
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
};

const pareceMoeda = (valor) =>
  typeof valor === "string" &&
  (/^\s*[R$€¥£]\s*[\d.,\s]+/.test(valor) || /[\d]+[.,]\d{2}\s*$/.test(valor));

const pareceNumero = (valor) => {
  if (typeof valor === "number") return Number.isFinite(valor);
  if (typeof valor !== "string") return false;
  const s = valor.trim();
  if (!s) return false;
  // eslint-disable-next-line no-useless-escape
  return /^-?\d{1,3}(?:[\.,]\d{3})*(?:[.,]\d+)?$|^-?\d+(?:[.,]\d+)?$/.test(s);
};

const paraNumero = (valor) => {
  if (typeof valor === "number") return valor;
  if (typeof valor !== "string") return NaN;
  let s = valor.trim().replace(/[R$€¥£\s]/g, "");
  if (!s) return NaN;
  if (/,\d{1,2}$/.test(s) && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (/,\d{1,2}$/.test(s)) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const detectarTipo = (valor, campoAlterado = "") => {
  if (valor === null || valor === undefined || valor === "") return "texto";
  if (campoAlterado && ehChaveMoeda(campoAlterado)) return "moeda";
  if (parseDataSegura(valor)) return "data";
  if (pareceMoeda(valor)) return "moeda";
  if (pareceNumero(valor)) {
    const n = paraNumero(valor);
    return Number.isFinite(n) ? "numero" : "texto";
  }
  return "texto";
};

const obterValorParaXlsx = (valor, campoAlterado = "") => {
  if (valor === null || valor === undefined || valor === "") {
    return { valor: "", formato: undefined };
  }
  const tipo = detectarTipo(valor, campoAlterado);
  if (tipo === "data") {
    return { valor: parseDataSegura(valor), formato: "dd/mm/yyyy hh:mm" };
  }
  if (tipo === "moeda") {
    return { valor: paraNumero(valor), formato: '"R$" #,##0.00' };
  }
  if (tipo === "numero") {
    return { valor: paraNumero(valor), formato: "#,##0.######" };
  }
  return { valor: String(valor), formato: undefined };
};

const HistoricoEventos = () => {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [itens, setItens] = useState([]);
  const [erroApi, setErroApi] = useState("");
  const [filtroCnpjCpf, setFiltroCnpjCpf] = useState("");
  const [filtroCnpjCpfAplicado, setFiltroCnpjCpfAplicado] = useState("");
  const [filtroAutor, setFiltroAutor] = useState("__SELECIONE__");
  const [filtroAutorAplicado, setFiltroAutorAplicado] =
    useState("__SELECIONE__");
  const [filtroEvento, setFiltroEvento] = useState("__SELECIONE__");
  const [filtroEventoAplicado, setFiltroEventoAplicado] =
    useState("__SELECIONE__");
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);

  const buscarEventos = async (cnpjCpf = "", autor = "", evento = "") => {
    setCarregando(true);
    try {
      const token = localStorage.getItem("token");
      const cnpjNumeros = String(cnpjCpf).replace(/\D/g, "");

      // INSTRUMENTAÇÃO TEMPORÁRIA – INÍCIO (remover após diagnóstico)
      // Mantém rastreamento em memória: para cada página da API,
      // guarda (id -> página(s)) e (id -> { página -> índices }).
      const __diag = {
        recebidoPorPagina: [], // [{ pagina, qtd }]
        idParaPaginas: new Map(), // Map<id, Set<page>>
        idParaIndicesPorPagina: new Map(), // Map<id, Map<page, number[]>>
        totalRegistros: 0,
        idsSemId: 0,
        __registrarItem(pagina, idxNaPagina, item) {
          if (!item || typeof item !== "object") return;
          const id = item.id;
          if (id === null || id === undefined || id === "") {
            this.idsSemId += 1;
            return;
          }
          if (!this.idParaPaginas.has(id)) {
            this.idParaPaginas.set(id, new Set());
            this.idParaIndicesPorPagina.set(id, new Map());
          }
          const setPag = this.idParaPaginas.get(id);
          setPag.add(pagina);
          const mapPorPag = this.idParaIndicesPorPagina.get(id);
          if (!mapPorPag.has(pagina)) mapPorPag.set(pagina, []);
          mapPorPag.get(pagina).push(idxNaPagina);
        },
        __registrarPagina(pagina, dados) {
          let qtd = 0;
          if (Array.isArray(dados)) {
            qtd = dados.length;
            dados.forEach((item, idx) =>
              this.__registrarItem(pagina, idx, item),
            );
          }
          this.recebidoPorPagina.push({ pagina, qtd });
          this.totalRegistros += qtd;
        },
      };
      // INSTRUMENTAÇÃO TEMPORÁRIA – FIM

      const params = new URLSearchParams();
      if (cnpjNumeros) params.append("cpf_cnpj", cnpjNumeros);
      if (autor) params.append("autor", autor);
      if (evento) params.append("evento", evento);

      const url = `${API_BASE_URL}/historico-eventos${params.toString() ? `?${params.toString()}` : ""}`;
      console.log("[HistoricoEventos] GET", url, "| cnpjNumeros=", cnpjNumeros);

      try {
        const primeiraResp = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // A API pode responder com:
        //  - array simples: [...]
        //  - paginado: { total, page, limit, pages, data: [...] }
        //  - erro: { errors: ["..."] } (HTTP 200 mesmo assim)
        const payload = primeiraResp.data;
        // DEBUG temporário: ver o que a API está devolvendo de verdade.
        console.log("[HistoricoEventos] payload bruto (página 1):", payload);

        // Se o backend já devolveu tudo em um único array, usa direto.
        if (Array.isArray(payload)) {
          setItens(payload);
          return;
        }

        // Resposta com erro explícito.
        if (payload?.errors) {
          setErroApi(payload.errors.join(" • "));
          setItens([]);
          return;
        }

        // Resposta paginada { total, page, limit, pages, data }.
        if (Array.isArray(payload?.data)) {
          const totalPages = Number(payload.pages) || 1;
          let lista = [...payload.data];
          // INSTRUMENTAÇÃO TEMPORÁRIA – INÍCIO (registrar página 1)
          __diag.__registrarPagina(1, payload.data);
          // INSTRUMENTAÇÃO TEMPORÁRIA – FIM
          // Percorre as páginas restantes em paralelo (se houver mais que 1).
          // Cada requisição é iniciada imediatamente; a ordem final na lista
          // segue a mesma sequência numérica das páginas (2, 3, ... N),
          // independentemente da ordem em que as respostas chegam.
          if (totalPages > 1) {
            const promessasPaginas = [];
            for (let p = 2; p <= totalPages; p += 1) {
              const proxParams = new URLSearchParams();
              if (cnpjNumeros) proxParams.append("cpf_cnpj", cnpjNumeros);
              if (autor) proxParams.append("autor", autor);
              if (evento) proxParams.append("evento", evento);
              proxParams.append("page", String(p));
              const proxUrl = `${API_BASE_URL}/historico-eventos?${proxParams.toString()}`;
              promessasPaginas.push(
                axios
                  .get(proxUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                  .then((proxResp) => {
                    const proxDados = proxResp.data?.data || proxResp.data;
                    // INSTRUMENTAÇÃO TEMPORÁRIA – INÍCIO (registrar página p)
                    __diag.__registrarPagina(p, proxDados);
                    // INSTRUMENTAÇÃO TEMPORÁRIA – FIM
                    return { pagina: p, dados: proxDados };
                  })
                  .catch((errPagina) => {
                    // Falha em uma página específica: loga mas não derruba tudo.
                    console.warn(
                      "[HistoricoEventos] falha ao buscar página",
                      p,
                      errPagina,
                    );
                    // INSTRUMENTAÇÃO TEMPORÁRIA – INÍCIO (registrar falha como 0)
                    __diag.__registrarPagina(p, []);
                    // INSTRUMENTAÇÃO TEMPORÁRIA – FIM
                    return { pagina: p, dados: [] };
                  }),
              );
            }

            const resultadosPaginas = await Promise.all(promessasPaginas);
            // Garante a mesma ordem do loop original (página 2 → página N),
            // mesmo que as respostas cheguem fora de ordem.
            resultadosPaginas
              .sort((a, b) => a.pagina - b.pagina)
              .forEach((res) => {
                if (Array.isArray(res.dados)) lista.push(...res.dados);
              });
          }
          console.log(
            "[HistoricoEventos] total itens após paginação:",
            lista.length,
            "| eventos únicos:",
            Array.from(new Set(lista.map((i) => i.evento))),
          );
          // INSTRUMENTAÇÃO TEMPORÁRIA – INÍCIO (relatório de duplicação)
          try {
            const totalPaginas = __diag.recebidoPorPagina.length;
            const idsUnicos = __diag.idParaPaginas.size;
            const duplicados = [];
            for (const [id, setPaginas] of __diag.idParaPaginas.entries()) {
              const ocorrencias =
                __diag.idParaIndicesPorPagina.get(id) || new Map();
              let total = 0;
              for (const arr of ocorrencias.values()) total += arr.length;
              if (total > 1 || setPaginas.size > 1) {
                const porPagina = {};
                for (const [pag, arr] of ocorrencias.entries()) {
                  porPagina[pag] = Array.from(new Set(arr)).sort(
                    (a, b) => a - b,
                  );
                }
                duplicados.push({
                  id,
                  paginas: Array.from(setPaginas).sort((a, b) => a - b),
                  total,
                  porPagina,
                });
              }
            }
            duplicados.sort((a, b) => Number(a.id) - Number(b.id));
            console.groupCollapsed(
              `%c[HistoricoEventos][DIAG-DUP] páginas=${totalPaginas} registros=${__diag.totalRegistros} ids únicos=${idsUnicos} ids duplicados=${duplicados.length} ids sem id=${__diag.idsSemId}`,
              "color:#d97706;font-weight:bold",
            );
            console.log(
              "[DIAG-DUP] por página (__diag.recebidoPorPagina):",
              __diag.recebidoPorPagina,
            );
            console.table(
              duplicados.map((d) => ({
                id: d.id,
                paginas: d.paginas.join(", "),
                ocorrencias_total: d.total,
              })),
            );
            for (const d of duplicados) {
              const dentro = d.paginas.length === 1;
              if (dentro) {
                console.log(
                  `[DUPLICADOS] ID ${d.id} -> página [${d.paginas.join(", ")}], ocorrências [${(d.porPagina[d.paginas[0]] || []).join(", ")}]`,
                );
              } else {
                const resumo = d.paginas
                  .map((p) => `${p}:[${(d.porPagina[p] || []).join(",")}]`)
                  .join(" ");
                console.log(
                  `[DUPLICADOS] ID ${d.id} -> páginas [${d.paginas.join(", ")}] (${resumo})`,
                );
              }
            }
            console.groupEnd();
          } catch (eDiag) {
            console.warn(
              "[HistoricoEventos][DIAG-DUP] falhou ao gerar relatório",
              eDiag,
            );
          }
          // INSTRUMENTAÇÃO TEMPORÁRIA – FIM
          setItens(lista);
          return;
        }

        // Sequelize findAndCountAll { count, rows }.
        if (Array.isArray(payload?.rows)) {
          setItens(payload.rows);
          return;
        }

        setItens([]);
      } catch (err) {
        if (err?.response?.status !== 404 && err?.response?.status !== 405) {
          throw err;
        }
      }

      setItens([]);
    } catch (err) {
      console.error("[HistoricoEventos] erro geral:", err);
      setItens([]);
    } finally {
      setCarregando(false);
    }
  };

  // Quando o usuário logado é o estudante (TCE / TCA Empregador), pré-
  // aplicamos o CPF dele no filtro para evitar mistura de eventos entre
  // diferentes autorizações. Os demais perfis (empresa / CIEE / consultor)
  // continuam vendo o filtro vazio, como antes.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      const isEstudante =
        decoded?.tipo === "estudante" ||
        Number(decoded?.acesso_autorizacao ?? 0) === 1;

      if (!isEstudante) return;

      const cpfEstudante = String(
        decoded?.cpf ||
          decoded?.documento ||
          decoded?.sub ||
          "",
      )
        .replace(/\D/g, "")
        .slice(0, 11);

      if (!cpfEstudante) return;

      setFiltroCnpjCpf(cpfEstudante);
      setFiltroCnpjCpfAplicado(cpfEstudante);
      setFiltrosAplicados(true);
      buscarEventos(cpfEstudante, "", "");
    } catch (e) {
      /* silencioso: fallback para fluxo padrão */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarFiltro = () => {
    const autorAplicado = filtroAutor === "__SELECIONE__" ? "" : filtroAutor;

    const eventoAplicado = filtroEvento === "__SELECIONE__" ? "" : filtroEvento;

    setFiltroCnpjCpfAplicado(filtroCnpjCpf.trim());
    setFiltroAutorAplicado(autorAplicado);
    setFiltroEventoAplicado(eventoAplicado);
    setFiltrosAplicados(true);
    setErroApi("");

    buscarEventos(filtroCnpjCpf.trim(), autorAplicado, eventoAplicado);
  };

  const limparFiltro = () => {
    setFiltroCnpjCpf("");
    setFiltroCnpjCpfAplicado("");
    setFiltroAutor("__SELECIONE__");
    setFiltroAutorAplicado("__SELECIONE__");
    setFiltroEvento("__SELECIONE__");
    setFiltroEventoAplicado("__SELECIONE__");
    setFiltrosAplicados(false);
    buscarEventos("", "", "");
  };

  const itensFiltrados = useMemo(() => {
    let lista = itens;

    // IMPORTANTE: o filtro de cpf_cnpj é feito pelo backend (via querystring
    // em /historico-eventos?cpf_cnpj=...). Aqui NÃO filtramos de novo,
    // senaria duplicamos a lógica e podemos mascarar divergências entre
    // o que o backend entende como "match" e o que o front entende.
    if (filtroAutorAplicado && filtroAutorAplicado !== "__SELECIONE__") {
      lista = lista.filter(
        (item) =>
          String(item.autor || "").toUpperCase() ===
          filtroAutorAplicado.toUpperCase(),
      );
    }

    if (filtroEventoAplicado && filtroEventoAplicado !== "__SELECIONE__") {
      lista = lista.filter((item) => item.evento === filtroEventoAplicado);
    }

    return lista;
  }, [itens, filtroAutorAplicado, filtroEventoAplicado]);

  // Opções dos filtros derivadas dinamicamente dos dados reais retornados
  // pela API. Sem catálogo prévio no frontend: se o backend criar um novo
  // valor de `autor` ou `evento`, ele aparecerá automaticamente aqui assim
  // que vier em qualquer item da resposta.
  const opcoesAutor = useMemo(() => {
    const set = new Set();
    for (const item of itens) {
      const v = String(item?.autor ?? "").trim();
      if (v) set.add(v);
    }
    return [
      { value: "", label: "Todos os autores" },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [itens]);

  const opcoesEvento = useMemo(() => {
    const set = new Set();
    for (const item of itens) {
      const v = String(item?.evento ?? "").trim();
      if (v) set.add(v);
    }
    return [
      { value: "", label: "Todos os eventos" },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [itens]);

  const temFiltroAplicado =
    filtrosAplicados &&
    (filtroCnpjCpfAplicado !== "" ||
      (filtroAutorAplicado !== "" && filtroAutorAplicado !== "__SELECIONE__") ||
      (filtroEventoAplicado !== "" &&
        filtroEventoAplicado !== "__SELECIONE__"));

  const obterDataHora = (item) =>
    item.data ||
    item.data_hora ||
    item.dataHora ||
    item.created_at ||
    item.createdAt ||
    item.timestamp ||
    item.updated_at ||
    null;

  const obterCnpjCpf = (item) =>
    item.cpf_cnpj || item.cnpj_cpf || item.cpfCnpj || "";

  const obterUsuario = (item) =>
    item.usuario_nome || item.usuario || item.nome_usuario || item.nome || "";

  // Monta a planilha em memória. Cada chave do objeto vira uma coluna,
  // e a biblioteca xlsx cuida de tudo: tipos, datas, acentos, células.
  const exportarXlsx = async () => {
    try {
      const cabecalho = [
        "Nome do Usuário",
        "CPF ou CNPJ",
        "Data/Hora",
        "Campo Alterado",
        "Valor Anterior",
        "Valor Novo",
        "Autor",
        "Descrição",
        "Status",
      ];

      const linhas = itensFiltrados.map((item) => {
        const anterior = obterValorParaXlsx(
          item.valor_anterior,
          item.campo_alterado,
        );
        const novo = obterValorParaXlsx(item.valor_novo, item.campo_alterado);
        return {
          "Nome do Usuário": obterUsuario(item),
          "CPF ou CNPJ": formatarCnpjCpf(obterCnpjCpf(item)),
          "Data/Hora": obterDataHora(item),
          "Campo Alterado": formatarCampoAlterado(item.campo_alterado),
          "Valor Anterior": anterior.valor,
          "Valor Novo": novo.valor,
          Autor: item.autor || "",
          Descrição: item.descricao || "—",
          Status: item.status ?? "",
        };
      });

      const planilha = XLSX.utils.json_to_sheet(linhas, { header: cabecalho });

      // Aplica formato nativo do Excel nas colunas dinâmicas.
      const colValorAnterior = cabecalho.indexOf("Valor Anterior");
      const colValorNovo = cabecalho.indexOf("Valor Novo");
      const colDataHora = cabecalho.indexOf("Data/Hora");

      itensFiltrados.forEach((item, rowIdx) => {
        const r = rowIdx + 1; // +1 porque a linha 0 é o cabeçalho
        if (colValorAnterior >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colValorAnterior, r });
          const fmt = obterValorParaXlsx(
            item.valor_anterior,
            item.campo_alterado,
          ).formato;
          if (planilha[ref] && fmt) planilha[ref].z = fmt;
        }
        if (colValorNovo >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colValorNovo, r });
          const fmt = obterValorParaXlsx(
            item.valor_novo,
            item.campo_alterado,
          ).formato;
          if (planilha[ref] && fmt) planilha[ref].z = fmt;
        }
        if (colDataHora >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colDataHora, r });
          const dh = obterDataHora(item);
          if (planilha[ref] && dh) {
            const d = new Date(dh);
            if (!isNaN(d.getTime())) {
              planilha[ref].t = "d";
              planilha[ref].v = d;
              planilha[ref].z = "dd/mm/yyyy hh:mm";
            }
          }
        }
      });

      // Largura automática das colunas (em caracteres).
      planilha["!cols"] = [
        { wch: 30 }, // Nome do Usuário
        { wch: 18 }, // CPF ou CNPJ
        { wch: 20 }, // Data/Hora
        { wch: 28 }, // Campo Alterado
        { wch: 30 }, // Valor Anterior
        { wch: 30 }, // Valor Novo
        { wch: 14 }, // Autor
        { wch: 40 }, // Atividade
        { wch: 12 }, // Status
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, planilha, "Histórico de Eventos");

      XLSX.writeFile(
        workbook,
        `historico-eventos_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (err) {
      // Falha ao exportar XLSX: silenciosa para o usuário final.
    }
  };

  return (
    <main className="container-historico-eventos">
      <div className="historico-topbar">
        <button
          type="button"
          className="historico-btn-voltar"
          onClick={() => {
            // Para o estudante, voltar do Histórico leva para a tela
            // de Preenchimento (TCE / TCA Emp.), não para o dashboard
            // geral (que é do CIEE/empresa).
            try {
              const tk = localStorage.getItem("token");
              if (tk) {
                const dec = jwtDecode(tk);
                if (
                  dec?.tipo === "estudante" ||
                  Number(dec?.acesso_autorizacao ?? 0) === 1
                ) {
                  const tp = Number(dec?.tipo_programa ?? 0);
                  navigate(tp === 2 ? "/tca-empregador" : "/tce", {
                    replace: false,
                  });
                  return;
                }
              }
            } catch (_) {
              /* fallback */
            }
            navigate("/dashboard");
          }}
        >
          <FaArrowLeft /> Voltar
        </button>
      </div>
      <header className="historico-header">
        <div className="historico-titulo-bloco">
          <h1>
            <FaHistory /> Histórico de Registros
          </h1>
          <p className="historico-subtitulo">
            Rastreamento de ações realizadas no sistema.
          </p>
        </div>

        <div className="historico-acoes-topo">
          <button
            type="button"
            className="historico-botao historico-botao-primario"
            onClick={exportarXlsx}
            disabled={carregando}
          >
            <FaFileExcel /> Exportar Documento
          </button>
        </div>
      </header>

      <section className="historico-filtros">
        <div className="historico-filtro-linha">
          <div className="historico-filtro-grupo historico-filtro-grupo--cresce">
            <label htmlFor="filtro-evento-cnpj-cpf">
              FILTRAR POR EMPRESA (CPF/CNPJ)
            </label>
            <input
              id="filtro-evento-cnpj-cpf"
              type="text"
              placeholder="00.000.000/0000-00"
              value={filtroCnpjCpf}
              onChange={(e) => setFiltroCnpjCpf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") aplicarFiltro();
              }}
            />
          </div>

          <div className="historico-filtro-grupo">
            <label htmlFor="filtro-evento-autor">AUTOR</label>
            <select
              id="filtro-evento-autor"
              value={filtroAutor}
              onChange={(e) => setFiltroAutor(e.target.value)}
            >
              <option value="__SELECIONE__" disabled>
                Selecione
              </option>
              {opcoesAutor.map((op) => (
                <option key={op.value || "todos-autores"} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <div className="historico-filtro-grupo historico-filtro-grupo--cresce">
            <label htmlFor="filtro-evento-tipo">EVENTO</label>
            <select
              id="filtro-evento-tipo"
              value={filtroEvento}
              onChange={(e) => setFiltroEvento(e.target.value)}
            >
              <option value="__SELECIONE__" disabled>
                Selecione
              </option>
              {opcoesEvento.map((op) => (
                <option key={op.value || "todos-eventos"} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="historico-filtrar"
            onClick={aplicarFiltro}
            disabled={carregando}
          >
            Filtrar
          </button>
          <button
            type="button"
            className="historico-limpar-filtros"
            onClick={limparFiltro}
            disabled={carregando}
          >
            Limpar
          </button>
        </div>
      </section>

      <section className="historico-informacoes">
        <div className="historico-informacoes-header">
          <FaHistory />
          <div>
            <h2>Informações do Histórico</h2>
            <p>
              O histórico registra as ações realizadas no cadastro e identifica
              o responsável, a data e o evento executado.
            </p>
          </div>
        </div>
        <div className="historico-informacoes-grid">
          <div className="historico-informacao-item">
            <strong>Usuário responsável</strong>
            <span>Identifica o usuário que realizou a ação no sistema.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Data e hora</strong>
            <span>Registra exatamente quando o evento foi realizado.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Evento realizado</strong>
            <span>Identifica a ação executada no cadastro.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Autor</strong>
            <span>
              Identifica se a ação foi realizada pela empresa, estudante, CIEE
              ou sistema.
            </span>
          </div>
          <div className="historico-informacao-item">
            <strong>CPF ou CNPJ</strong>
            <span>Permite identificar o cadastro relacionado ao evento.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Status</strong>
            <span>Apresenta o status relacionado ao evento registrado.</span>
          </div>
        </div>
      </section>

      <section className="historico-resultados">
        <p className="historico-resultados-titulo">REGISTROS ENCONTRADOS</p>
        {carregando ? (
          <div className="historico-loader">
            <Loader />
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="historico-vazio">
            <FaFileAlt size={48} />
            {erroApi ? (
              <p className="historico-erro-api">⚠️ {erroApi}</p>
            ) : temFiltroAplicado ? (
              <p>Nenhum registro encontrado.</p>
            ) : (
              <p>
                Use os filtros acima e clique em <strong>Filtrar</strong> para
                buscar os registros.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="historico-tabela-wrapper">
              <table className="historico-tabela">
                <thead>
                  <tr>
                    <th>Nome do Usuário</th>
                    <th>CPF ou CNPJ</th>
                    <th>Data/Hora</th>
                    <th>Campo Alterado</th>
                    <th>Valor Anterior</th>
                    <th>Valor Novo</th>
                    <th>Autor</th>
                    <th>Tipo</th>
                    <th>Ref. ID</th>
                    <th>Descrição</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {itensFiltrados.map((item) => (
                    <tr
                      key={
                        item.id ??
                        `${item.cpf_cnpj || item.cnpj_cpf}-${item.data_hora || item.data || item.created_at}-${item.evento}`
                      }
                    >
                      <td>
                        {item.usuario_nome ||
                          item.usuario ||
                          item.nome_usuario ||
                          "—"}
                      </td>
                      <td>
                        {formatarCnpjCpf(item.cpf_cnpj || item.cnpj_cpf) || "—"}
                      </td>
                      <td>
                        {formatarDataHora(
                          item.data_hora || item.data || item.created_at,
                        )}
                      </td>
                      <td>{item.campo_alterado ?? "—"}</td>
                      <td>{item.valor_anterior ?? "—"}</td>
                      <td>{item.valor_novo ?? "—"}</td>
                      <td>
                        <span
                          className={`badge-autor badge-autor-${(item.autor || "").toLowerCase()}`}
                        >
                          {item.autor || "—"}
                        </span>
                      </td>
                      <td>
                        {item.tipo === null || item.tipo === undefined
                          ? "—"
                          : item.tipo}
                      </td>
                      <td>{item.referencia_id ?? "—"}</td>
                      <td>{item.descricao || "—"}</td>
                      <td>
                        {item.status !== undefined && item.status !== null ? (
                          <span
                            className={`historico-badge-status historico-badge-status-${String(item.status).toLowerCase().replace(/\s/g, "-")}`}
                          >
                            {item.status}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <footer className="historico-rodape">
              <strong>REGISTROS ENCONTRADOS</strong>
              <span>
                Total: <strong>{itensFiltrados.length}</strong> registro(s)
              </span>
            </footer>
          </>
        )}
      </section>
    </main>
  );
};

export default HistoricoEventos;
